import {memo, useEffect} from 'react';

import Box from '@mui/material/Box';
import {useMeasure, useWindowSize} from '@uidotdev/usehooks';
import {useLocation} from 'react-router-dom';

import usePreventIframeClickPropagation from '../../../hooks/components/main-sidebar/usePreventIframeClickPropagation';
import {useSidebarDisplayLogic} from '../../../hooks/components/main-sidebar/useSidebarDisplayLogic';
import useMainSidebar from '../../../hooks/providers/useMainSidebar';
import useReport from '../../../hooks/providers/useReport';
import DeleteViewModal from '../../embed/report-views/DeleteViewModal/DeleteViewModal';
import MoreButtonsOnly from '../MoreButtonsOnly/MoreButtonsOnly';
import MoreReportsOrModulesPopper from '../MoreReportsOrModulesPopper/MoreReportsOrModulesPopper';
import NoProjectSelectedBackdrop from '../NoProjectSelectedBackdrop/NoProjectSelectedBackdrop';
import ReportModulesSidebarContainer from '../ReportModulesSidebarContainer/ReportModulesSidebarContainer';
import ReportsAndModulesStack from '../ReportsAndModulesStack/ReportsAndModulesStack';
import ReportsList from '../ReportsList/ReportsList';
import RequestNewModuleContactButton from '../RequestNewModuleContactButton/RequestNewModuleContactButton';
import SplitReportAndModules from '../SplitReportAndModules/SplitReportAndModules';

// eslint-disable-next-line complexity
const ReportModulesSidebar = () => {
  const [ref, {height: containerHeight}] = useMeasure();

  const location = useLocation();
  const windowSize = useWindowSize();
  const {shouldDisplayContactButton, closeAllSidebarRelatedPoppers} = useMainSidebar();
  const {reports} = useReport();

  const {totalHeightNeededForReportsDisplaying, shouldDisplayPanes, shouldDisplayOnlyMoreButtons} = useSidebarDisplayLogic(containerHeight);
  usePreventIframeClickPropagation();

  const isReportPage = location.pathname.startsWith('/reports');
  const shouldShowSplit = isReportPage && shouldDisplayPanes && !shouldDisplayOnlyMoreButtons;
  const shouldShowMoreButtons = isReportPage && shouldDisplayOnlyMoreButtons;
  const shouldShowStack = isReportPage && !shouldDisplayPanes && !shouldDisplayOnlyMoreButtons;
  const shouldShowList = !isReportPage;

  useEffect(() => {
    closeAllSidebarRelatedPoppers();
  }, [windowSize]);

  if (reports.length === 0) {
    return null;
  }

  return (
    <Box data-testid="main-sidebar">
      <ReportModulesSidebarContainer ref={ref}>
        <NoProjectSelectedBackdrop />

        {shouldShowSplit && <SplitReportAndModules totalHeightNeededForReportsDisplaying={totalHeightNeededForReportsDisplaying} />}
        {shouldShowMoreButtons && <MoreButtonsOnly />}
        {shouldShowStack && <ReportsAndModulesStack totalHeightNeededForReportsDisplaying={totalHeightNeededForReportsDisplaying} />}
        {shouldShowList && <ReportsList maxHeight={totalHeightNeededForReportsDisplaying} />}

        <MoreReportsOrModulesPopper isReportsPopper showAllItems={shouldDisplayOnlyMoreButtons} />
        <MoreReportsOrModulesPopper isReportsPopper={false} showAllItems={shouldDisplayOnlyMoreButtons} />
        <RequestNewModuleContactButton isVisible={isReportPage && shouldDisplayContactButton && !shouldDisplayOnlyMoreButtons} />
      </ReportModulesSidebarContainer>

      <DeleteViewModal />
    </Box>
  );
};

export default memo(ReportModulesSidebar);
